import { useMemoryBankContext } from '../../../../../contexts/MemoryBankContext/MemoryBankContext';
import { useMemoryIconsContext } from '../../../../../contexts/MemoryIconsContext/MemoryIconsContext';
import { BranchStep, BranchStepMemoryActionType } from '../../../../../dorian-shared/types/branch/BranchStep';
import { MemoryLinkValue, MemoryShowIn, MemoryType, MemoryValue } from '../../../Book/MemoryBank/memoryBankTypes';
import { getMemorySlotValue, isMemorySlotVariableLink, getMemorySlotVariableLink } from '../../../../utils/choiceMemoryUtils';
import { StepWithAction } from '../MemoryCard/MemoryCardTypes';
import { gerValueByActionType } from '../MemoryCard/utils';

export function useStepsWithAction(steps: BranchStep[]) {
  const { memories } = useMemoryBankContext();
  const { memoryIcons } = useMemoryIconsContext();

  return steps.reduce((acc: StepWithAction[], step) => {
    if (step.action) {
      const memory = memories.find((m) => Number(m.id) === Number(step.action?.variableId ?? 0));
      const icon = memoryIcons.find((m) => Number(m.id) === Number(memory?.icon ?? 0));
      const isMemoryNumeric = memory?.type === MemoryType.Number;
      const isValueArray = Array.isArray(step.action.value);

      const memoryExistsIndex = acc.findIndex((m) => m.memoryId === memory?.id);
      const isVariableLink = !isValueArray && isMemorySlotVariableLink(step.action.value as MemoryValue | MemoryLinkValue);
      let value: string | number | MemoryShowIn[];
      if (isValueArray) {
        value = step.action.value as MemoryShowIn[];
      } else if (isVariableLink) {
        const link = getMemorySlotVariableLink(step.action.value as MemoryValue | MemoryLinkValue);
        const refMemory = memories.find((m) => Number(m.id) === Number(link?.variableId));
        value = refMemory?.name ?? '?';
      } else {
        value = getMemorySlotValue(step.action.value as MemoryValue | MemoryLinkValue);
      }
      if (isMemoryNumeric && !isValueArray && !isVariableLink) {
        value = Number(value);
      }
      const isToggleShowIn = step.action.type === BranchStepMemoryActionType.View;
      const memoryType = memory?.type;
      const currentValue = acc[memoryExistsIndex]?.value ?? '';
      if (!isVariableLink) {
        value = gerValueByActionType(step.action.type, value, currentValue);
      }

      if (memoryExistsIndex > -1) {
        acc[memoryExistsIndex] = {
          ...acc[memoryExistsIndex],
          value: value as MemoryValue,
          isToggleShowIn: isToggleShowIn || acc[memoryExistsIndex].isToggleShowIn,
          memoryType: memoryType ?? acc[memoryExistsIndex].memoryType,
        };
        return acc;
      }

      acc.push({
        id: step.id,
        type: 'card',
        memoryId: memory?.id ?? 0,
        memoryName: memory?.name ?? '',
        memoryIconUrl: icon?.url ?? '',
        value: isToggleShowIn ? '0' : value as MemoryValue,
        isToggleShowIn,
        memoryType,
      });
    }

    // Handle Random steps (with roll)
    if (step.roll) {
      const memory = memories.find((m) => Number(m.id) === Number(step.roll?.variableId ?? 0));
      const icon = memoryIcons.find((m) => Number(m.id) === Number(memory?.icon ?? 0));

      const memoryExistsIndex = acc.findIndex((m) => m.memoryId === memory?.id);
      // Create display string based on roll type
      let range: string;
      if (step.roll.type === 'dice') {
        range = `${'diceNotation' in step.roll ? step.roll.diceNotation : '?'}`;
      } else {
        range = `${'min' in step.roll ? step.roll.min : '?'}-${'max' in step.roll ? step.roll.max : '?'}${step.roll.modifier ? (step.roll.modifier >= 0 ? '+' + step.roll.modifier : step.roll.modifier) : ''}`;
      }

      if (memoryExistsIndex > -1) {
        acc[memoryExistsIndex] = {
          ...acc[memoryExistsIndex],
          value: range as MemoryValue,
          isToggleShowIn: false, // Random steps don't have toggle view
          rollType: step.roll.type, // Pass the actual enum value
        };
        return acc;
      }

      acc.push({
        id: step.id,
        type: 'card',
        memoryId: memory?.id ?? 0,
        memoryName: memory?.name ?? '',
        memoryIconUrl: icon?.url ?? '',
        value: range as MemoryValue,
        isToggleShowIn: false, // Random steps don't have toggle view
        rollType: step.roll.type, // Pass the actual enum value
      });
    }
    
    return acc;
  }, []);
}
